import { motion } from "framer-motion";
import { ArrowRight, CalendarDays } from "lucide-react";
import Seo from "@/components/Seo";
import { absoluteUrl } from "@/lib/site-config";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import { useDemoModal } from "@/context/DemoModalContext";

/** Fundadores mostrados en la sección «Quiénes somos». */
const FOUNDERS = [
  {
    initials: "OE",
    name: "Osmel Enamorado",
    role: "Fundador y desarrollador",
    bio: "Yo aporto la tecnología. Juntos construimos algo que tiene sentido para ustedes.",
  },
  {
    initials: "LV",
    name: "Leydy Vargas",
    role: "Directora de Diseño y Relaciones Pastorales",
    bio: "Leydy aporta algo que ningún manual de software puede enseñar: experiencia real en el entorno pastoral y el conocimiento de cómo funciona una iglesia por dentro. Ella aporta el contexto.",
  },
];

/** Mismo patrón de entrada usado en las secciones del landing. */
const fade = {
  initial: { opacity: 0, y: 14 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-40px" },
  transition: { duration: 0.75 },
};

export default function QuienesSomosPage() {
  const { openModal } = useDemoModal();

  return (
    <div className="w-full overflow-x-hidden bg-white text-ink">
      <Seo
        title="Quiénes somos"
        description="EkklesiaOnline no nació en una oficina, nació en una iglesia. Conoce a Osmel y Leydy y por qué construimos un software de gestión pensado para las iglesias hispanohablantes."
        path="/quienes-somos"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "AboutPage",
          name: "Quiénes somos | EkklesiaOnline",
          description:
            "La historia detrás de EkklesiaOnline y el matrimonio que lo construye: Osmel Enamorado y Leydy Vargas.",
          url: absoluteUrl("/quienes-somos"),
        }}
      />
      <Header />

      {/* ── 1. Hero (oscuro, mismo tratamiento que las secciones oscuras del landing) ── */}
      <section
        data-testid="about-hero"
        className="relative overflow-hidden px-4 pb-20 pt-32 sm:px-6 md:px-12 md:pb-28 md:pt-40"
        style={{
          background:
            "linear-gradient(165deg, #1e0a3c 0%, #2E2458 55%, #1e0a3c 100%)",
        }}
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute left-0 top-0 h-1 w-full bg-gradient-to-r from-transparent via-gold/80 to-transparent"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-60"
          style={{
            background:
              "radial-gradient(ellipse 70% 50% at 25% 10%, rgba(201,162,39,0.12) 0%, transparent 50%)",
          }}
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-40"
          style={{
            background:
              "radial-gradient(ellipse 50% 60% at 80% 90%, rgba(74,58,138,0.35) 0%, transparent 55%)",
          }}
        />

        <div className="relative z-10 mx-auto max-w-3xl text-center">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
            className="mb-5 text-sm font-semibold uppercase tracking-[0.2em] text-gold"
          >
            Sobre nosotros
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.06 }}
            className="font-display text-4xl font-black leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-6xl"
          >
            Construida desde adentro.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.14 }}
            className="mx-auto mt-6 max-w-xl text-xl leading-relaxed text-white/60 md:text-2xl"
          >
            EkklesiaOnline no nació en una oficina. Nació en una iglesia.
          </motion.p>
        </div>
      </section>

      {/* ── 2. Nuestra historia ── */}
      <section
        data-testid="about-historia"
        className="px-4 py-20 sm:px-6 md:px-12 md:py-32"
      >
        <div className="mx-auto max-w-3xl">
          <motion.div {...fade} className="mb-10 text-center">
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.2em] text-gold">
              Nuestra historia
            </p>
            <h2 className="font-display text-3xl font-bold tracking-tight text-ink sm:text-4xl">
              Por qué empezamos
            </h2>
          </motion.div>

          <motion.div
            {...fade}
            className="space-y-6 text-lg leading-relaxed text-ink-2 md:text-xl"
          >
            <p>
              Vivimos de cerca cómo funcionan las iglesias por dentro. La
              información llega por muchos lados, cada persona gestiona las cosas
              a su manera, con su propio sistema. Y cuando necesitas saber algo
              concreto, como cuántos miembros hay, qué nuevas personas han venido
              este domingo, cómo van las finanzas... normalmente nadie tiene una
              respuesta concreta, porque no existe un único lugar donde mirar.
            </p>
            <p>
              Ahí entendimos algo importante: las iglesias necesitan una
              herramienta que se adapte a cómo ellas ya funcionan.
            </p>
            <p>
              Y cuando esa herramienta no existe, el coste es real en tiempo y en
              dolores de cabeza. Cada visitante que nadie llamó es una persona
              que se fue sin sentirse completamente bienvenida. Cada euro que no
              se registró correctamente es una conversación incómoda en la
              próxima reunión. El desorden administrativo no es un inconveniente
              menor, es lo que te roba, a ti pastor, el tiempo que sabemos que le
              quieres dedicar a las personas, al estudio de la Palabra para crear
              enseñanzas y capacitaciones, y el tiempo a tu familia.
            </p>
            <p>
              Buscamos si existía algo que resolviera esto para las iglesias
              hispanohablantes. Había opciones, la mayoría en inglés, con
              interfaces complejas, pensadas de otra manera. Nada que una iglesia
              española o latinoamericana pudiera usar con facilidad, sin
              necesitar un experto en tecnología y encima en otro idioma.
            </p>

            <blockquote className="border-l-4 border-gold bg-violet-light/40 py-5 pl-6 pr-4">
              <p className="font-display text-xl font-semibold italic text-violet-fuerte md:text-2xl">
                Entonces pensamos: «¿Por qué no lo creamos nosotros?»
              </p>
            </blockquote>

            <p className="font-semibold text-ink">Eso es EkklesiaOnline.</p>
          </motion.div>
        </div>
      </section>

      {/* ── 3. Quiénes somos (fondo gris/violeta suave, cards fundadores) ── */}
      <section
        data-testid="about-equipo"
        className="px-4 py-20 sm:px-6 md:px-12 md:py-32"
        style={{
          background:
            "linear-gradient(168deg, rgba(238,235,255,0.5) 0%, #FAFAFE 45%, #ffffff 100%)",
        }}
      >
        <div className="mx-auto max-w-5xl">
          <motion.div {...fade} className="mx-auto mb-14 max-w-3xl text-center">
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.2em] text-gold">
              El equipo
            </p>
            <h2 className="font-display text-3xl font-bold tracking-tight text-ink sm:text-4xl">
              Quiénes somos
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-ink-2 md:text-xl">
              Somos Osmel Enamorado y Leydy Vargas, un matrimonio joven
              cristiano. Llevamos años dentro de la iglesia, yo 7, Leydy más de
              10. Sabemos lo que es ver las necesidades de una congregación de
              cerca y entender que el problema es que sientes que hacen falta más
              horas al día para hacer todo lo que aún falta. Tienes muchos
              frentes abiertos que resolver y no sabes cómo simplificarlo. Te
              faltaba una herramienta que te ayude de verdad, que te quite horas
              de tareas y te facilite tu labor.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
            {FOUNDERS.map((person, i) => (
              <motion.div
                key={person.initials}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.75, delay: i * 0.1 }}
                data-testid={`founder-card-${person.initials.toLowerCase()}`}
                className="flex flex-col items-center rounded-2xl border border-violet-light bg-white p-8 text-center shadow-[0_12px_40px_-16px_rgba(74,58,138,0.16),0_4px_16px_-8px_rgba(74,58,138,0.08)]"
              >
                <div
                  aria-hidden="true"
                  className="flex h-20 w-20 items-center justify-center rounded-full bg-violet-fuerte font-display text-xl font-bold text-white shadow-[0_8px_24px_-8px_rgba(74,58,138,0.35)]"
                >
                  {person.initials}
                </div>
                <h3 className="mt-6 font-display text-2xl font-bold tracking-tight text-ink">
                  {person.name}
                </h3>
                <p className="mt-1.5 text-sm font-semibold uppercase tracking-[0.12em] text-violet-fuerte">
                  {person.role}
                </p>
                <p className="mt-4 text-lg leading-relaxed text-ink-2">
                  {person.bio}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 4. Por qué existe (fondo violeta suave, centrado) ── */}
      <section
        data-testid="about-porque"
        className="bg-violet-light px-4 py-20 sm:px-6 md:px-12 md:py-32"
      >
        <motion.div {...fade} className="mx-auto max-w-3xl text-center">
          <p className="mb-4 text-sm font-semibold uppercase tracking-[0.2em] text-gold-text">
            Nuestro propósito
          </p>
          <h2 className="font-display text-3xl font-bold tracking-tight text-ink sm:text-4xl">
            Por qué existe EkklesiaOnline
          </h2>
          <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-ink-2 md:text-xl">
            No creamos esto para vender un software cualquiera. Lo creamos porque
            vivimos el problema desde adentro y sabemos exactamente lo que
            necesitan: una herramienta simple, en español, que permita dedicar
            menos tiempo al desorden y más tiempo a las personas.
          </p>
          <p className="mt-8 font-display text-2xl font-bold tracking-tight text-violet-fuerte md:text-3xl">
            Porque eso es lo que realmente importa.
          </p>
        </motion.div>
      </section>

      {/* ── 5. CTA final (mismo botón primario de marca → registro/prueba) ── */}
      <section
        data-testid="about-cta"
        className="px-4 py-20 sm:px-6 md:px-12 md:py-32"
      >
        <motion.div {...fade} className="mx-auto max-w-2xl text-center">
          <h2 className="font-display text-3xl font-bold tracking-tight text-ink sm:text-4xl">
            ¿Listo para simplificar la iglesia?
          </h2>
          <p className="mx-auto mt-5 max-w-lg text-lg leading-relaxed text-ink-2 md:text-xl">
            Empieza con datos de ejemplo y descubre cómo sería tener todo en un
            solo lugar. Sin tarjeta, sin compromiso.
          </p>
          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <button
              type="button"
              onClick={openModal}
              data-testid="about-cta-button"
              className="inline-flex h-[52px] items-center justify-center gap-2 rounded-xl bg-violet-fuerte px-8 text-[17px] font-semibold text-white shadow-[0_6px_24px_-6px_rgba(74,58,138,0.45)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-violet-oscuro hover:shadow-[0_10px_32px_-6px_rgba(74,58,138,0.5)]"
            >
              Prueba EkklesiaOnline gratis
              <ArrowRight className="h-5 w-5" />
            </button>
            <button
              type="button"
              data-cal-link="ekklesiaonline-demo"
              data-cal-config='{"layout":"month_view"}'
              data-testid="about-cta-agenda"
              className="inline-flex h-[52px] items-center justify-center gap-2 rounded-xl border border-violet-light bg-white px-8 text-[17px] font-semibold text-ink-2 transition-all duration-300 hover:-translate-y-0.5 hover:border-violet-fuerte/40 hover:text-violet-fuerte"
            >
              <CalendarDays className="h-5 w-5" />
              Agenda una llamada
            </button>
          </div>
          <p className="mt-8 text-[15px] text-ink-3">
            Sin tarjeta &middot; Sin compromiso &middot; Acceso en minutos
          </p>
        </motion.div>
      </section>

      <Footer />
    </div>
  );
}
